import { useEffect, useMemo, useState } from 'react';

import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import Invitations from '@zola/zola-ui/src/assets/images/icons/invitations.svg';
import WeddingWebsites from '@zola/zola-ui/src/assets/images/icons/wedding_websites.svg';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import Collage1x from '~/assets/images/real-weddings/unsupplied_market_collage_1x.png';
import Collage2x from '~/assets/images/real-weddings/unsupplied_market_collage_2x.png';
import { CtaType, trackCtaClicked, TrackingProductLocation } from '~/util/trackingHelper';

import styles from './unsuppliedMarketModal.module.less';

export interface UnsuppliedMarketModalProps {
  showRealWeddings: () => void;
  marketName: string;
}

const UnsuppliedMarketModal = ({ showRealWeddings, marketName }: UnsuppliedMarketModalProps) => {
  const cta = useMemo(
    () => ({
      ctaType: CtaType.BUTTON,
      location: TrackingProductLocation.UNSUPPLIED_MARKET_MODAL,
    }),
    []
  );

  const trackClick = (ctaId: string, position: number) => {
    trackCtaClicked({ ...cta, ctaId, position });
  };

  const [modalCopy, setModalCopy] = useState({
    modalHed: `We’re adding new vendors in ${marketName} soon! Keep the ball rolling with wedding inspo that feels like you!`,
    invitesSubHed: 'Want save the dates as unique as you two?',
    invitesDek:
      'We let you personalize with photos, colors, fonts, and paper types (even magnets!).',
    invitesCta: 'Browse Our Designs',
    websitesSubHed: 'Need to share details with guests?',
    websitesDek: 'Add all the info they need to a free wedding website where they can RSVP, too.',
    websitesCta: 'Create a Website',
  });

  useEffect(() => {
    if (isMobile()) {
      setModalCopy({
        modalHed: `We’re adding new vendors in ${marketName} soon! Want wedding inspo that feels like you?`,
        invitesSubHed: 'You’ll Love Our Paper, Too',
        invitesDek: '',
        invitesCta: 'Start Your Save the Dates',
        websitesSubHed: 'Add a Free Matching Website',
        websitesDek: '',
        websitesCta: 'Build Your Wedding Website',
      });
    }
  }, [marketName]);

  return (
    <div className={styles.unsuppliedMarketModal}>
      <div className={styles.modalHeader}>
        <div className={styles.unsuppliedMarketModalHeaderImageContainer}>
          <div className={styles.unsuppliedMarketModalHeaderImage}>
            <img srcSet={`${Collage1x} 1x, ${Collage2x} 2x`} src={Collage2x} alt="" />
          </div>
          <div className={styles.unsuppliedMarketModalHeaderTextContainer}>
            <h3 className={styles.hed}>{modalCopy.modalHed}</h3>
            <ButtonV2 className={styles.cta} onClick={showRealWeddings}>
              Explore Real Weddings in {marketName}
            </ButtonV2>
          </div>
        </div>
      </div>
      <div className={styles.modalBody}>
        <div className={styles.ctaBlock}>
          <div className={styles.ctaIcon}>
            <img src={Invitations} alt="" />
          </div>
          <div className={styles.copyContainer}>
            <p className={styles.hed}>{modalCopy.invitesSubHed}</p>
            {modalCopy.invitesDek && <p className={styles.dek}>{modalCopy.invitesDek}</p>}
            <LinkV2
              onClick={() => trackClick('Browse Paper and Invites', 2)}
              href="/wedding-planning/save-the-date/shop"
              arrow
            >
              {modalCopy.invitesCta}
            </LinkV2>
          </div>
        </div>
        <div className={styles.ctaBlock}>
          <div className={styles.ctaIcon}>
            <img src={WeddingWebsites} alt="" />
          </div>
          <div className={styles.copyContainer}>
            <p className={styles.hed}>{modalCopy.websitesSubHed}</p>
            {modalCopy.websitesDek && <p className={styles.dek}>{modalCopy.websitesDek}</p>}
            <LinkV2
              onClick={() => trackClick('Create a website', 3)}
              href="/wedding-planning/website"
              arrow
            >
              {modalCopy.websitesCta}
            </LinkV2>
          </div>
        </div>
      </div>
      <div className="modal-footer" />
    </div>
  );
};

export default UnsuppliedMarketModal;
